const MAX_DIMENSION = 1280;

export const MAX_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024;
export const MAX_TOTAL_IMAGE_UPLOAD_BYTES = 8 * 1024 * 1024;

const IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]);

type PreparedImagesResult = {
  files: File[];
  errors: string[];
};

const bytesToMb = (bytes: number): string => (bytes / (1024 * 1024)).toFixed(1);

const readImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Kunne ikke indlæse billedet.'));
    };
    image.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Kunne ikke komprimere billedet.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });

const compressImage = async (file: File): Promise<File> => {
  if (file.type === 'image/gif') {
    return file;
  }

  const image = await readImage(file);
  const maxSourceDimension = Math.max(image.width, image.height);
  const initialScale = Math.min(1, MAX_DIMENSION / maxSourceDimension);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Kunne ikke oprette canvas til billedkomprimering.');
  }

  let scale = initialScale;
  const qualitySteps = [0.76, 0.68, 0.6, 0.52, 0.46, 0.4];

  for (let i = 0; i < 6; i++) {
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    for (const quality of qualitySteps) {
      const blob = await canvasToBlob(canvas, quality);
      const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      if (compressedFile.size <= MAX_IMAGE_UPLOAD_BYTES) {
        return compressedFile;
      }
    }

    scale *= 0.82;
  }

  return file;
};

export const prepareImageFiles = async (filesInput: FileList | File[]): Promise<PreparedImagesResult> => {
  const files = Array.from(filesInput);
  const validFiles: File[] = [];
  const errors: string[] = [];

  for (const file of files) {
    if (!IMAGE_TYPES.has(file.type)) {
      errors.push(`${file.name}: filtypen understøttes ikke.`);
      continue;
    }

    let processedFile = file;
    try {
      processedFile = await compressImage(processedFile);
    } catch {
      errors.push(`${file.name}: kunne ikke komprimeres.`);
      continue;
    }

    if (processedFile.size > MAX_IMAGE_UPLOAD_BYTES) {
      errors.push(
        `${file.name}: billedet er for stort (${bytesToMb(processedFile.size)} MB). Max ${bytesToMb(MAX_IMAGE_UPLOAD_BYTES)} MB.`
      );
      continue;
    }

    validFiles.push(processedFile);
  }

  return { files: validFiles, errors };
};